import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { MessageCircle } from "lucide-react";
import { apiService } from "@/services/api";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { registerRequestSchema } from "@/schemas/api";
import type { RegisterRequestInput } from "@/schemas/api";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const Register = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [countryCode, setCountryCode] = useState("972");

  const form = useForm<RegisterRequestInput>({
    resolver: zodResolver(registerRequestSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      password: "",
    },
  });

  const onSubmit = async (data: RegisterRequestInput) => {
    try {
      const formattedPhone = `${countryCode}${
        data.phone.startsWith("0") ? data.phone.slice(1) : data.phone
      }`;
      await apiService.register({ ...data, phone: formattedPhone });
      navigate("/login");
      toast({
        title: t("common.success"),
        description: t("register.success"),
      });
    } catch (error) {
      console.error(error);
      toast({
        title: t("common.error"),
        description:
          error instanceof Error ? error.message : t("register.failed"),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <MessageCircle className="h-12 w-12 text-whatsapp" />
          </div>
          <CardTitle className="text-2xl text-center">
            {t("register.create_account")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input placeholder={t("register.full_name")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input type="email" placeholder={t("register.email")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="flex gap-2">
                        <Input
                          type="tel"
                          className="w-24"
                          value={countryCode}
                          onChange={(e) =>
                            setCountryCode(e.target.value.replace(/\D/g, ""))
                          }
                          placeholder={t("register.code")}
                        />
                        <Input
                          type="tel"
                          placeholder={t("register.phone")}
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder={t("register.password")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="w-full bg-whatsapp hover:bg-whatsapp/90"
              >
                {t("register.sign_up")}
              </Button>
            </form>
          </Form>
          <div className="mt-4 text-center text-sm">
            <a href="/login" className="text-whatsapp hover:underline">
              {t("register.have_account")}
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
